import React, { useMemo } from 'react';
import Link from 'next/link';
import _ from 'lodash';
import { useRouter } from 'next/compat/router';
import { getPersistedValue } from 'shared/utils/persisted-values';
import { MARKETING_CAMPAIGN_PARAMS } from 'shared/constants';

/** Returns true if the path is an internal app path (not external URL). */
function isInternalPath(path) {
  if (!_.isString(path) || path.length === 0) {
    return false;
  }
  const trimmed = path.trim();
  return trimmed.startsWith('/') && !trimmed.startsWith('//');
}

/** Get current marketing/UTM params from router or persisted session. */
function getMarketingParamsForLink(router, persistedSessionData) {
  const fromQuery = _.pick(router?.query ?? {}, MARKETING_CAMPAIGN_PARAMS);
  if (!_.isEmpty(fromQuery)) {
    return fromQuery;
  }
  const fromSession = _.get(persistedSessionData, 'parameters', {});
  return _.pick(fromSession, MARKETING_CAMPAIGN_PARAMS);
}

/** Build string URL for anchor href when linkHref is { pathname, query }. */
function linkHrefToAnchorHref(linkHref) {
  if (_.isString(linkHref)) {
    return linkHref;
  }
  if (_.isObject(linkHref) && linkHref.pathname) {
    const path = linkHref.pathname;
    const { query } = linkHref;
    if (!query || _.isEmpty(query)) {
      return path;
    }
    const pairs = [];
    Object.keys(query).forEach((key) => {
      const value = query[key];
      const encodedKey = encodeURIComponent(key);
      if (Array.isArray(value)) {
        value.forEach((v) => pairs.push(`${encodedKey}=${encodeURIComponent(v)}`));
      } else if (value != null && value !== '') {
        pairs.push(`${encodedKey}=${encodeURIComponent(value)}`);
      }
    });
    return pairs.length ? `${path}?${pairs.join('&')}` : path;
  }
  return linkHref;
}

export default function BetterLink(props) {
  const { href, route, children, scroll = true, prefetch, ...urlProps } = props;
  const router = useRouter();
  const utmSessionData = useMemo(() => getPersistedValue('utmSessionData', null), []);

  // Detect if any direct child is an anchor (including styled-components anchored to 'a')
  const childrenArray = React.Children.toArray(children);
  const hasAnchorChild = childrenArray.some((child) => {
    if (!React.isValidElement(child)) {
      return false;
    }
    const { type } = child;
    // Treat styled-components anchors and native 'a' as anchors
    return (_.isString(type) && type === 'a') || (type && type.target === 'a');
  });

  const linkProps = {
    prefetch,
    scroll,
    legacyBehavior: true,
    passHref: true,
  };

  // Prefer a concrete href string when provided. Fallback to route + query object.
  // For internal links, merge in current UTM/marketing params so they persist across navigation.
  const linkHref = (() => {
    const { query: callerQuery } = urlProps || {};
    const isInternal = isInternalPath(href) || isInternalPath(route);

    const marketingParams = isInternal ? getMarketingParamsForLink(router, utmSessionData) : {};
    const mergedQuery =
      _.isEmpty(marketingParams) && _.isEmpty(callerQuery) ? null : { ...marketingParams, ...callerQuery };

    if (_.isString(href) && href.length > 0) {
      return mergedQuery ? { pathname: href, query: mergedQuery } : href;
    }

    if (route) {
      return mergedQuery ? { pathname: route, query: mergedQuery } : route;
    }

    return href;
  })();

  // Collect anchor attributes that should be forwarded to the injected <a> when
  // children are not already an anchor. This preserves testids, classes, events, etc.
  const anchorAttrKeys = Object.keys(urlProps).filter(
    (key) =>
      key === 'className' ||
      key === 'id' ||
      key === 'title' ||
      key === 'role' ||
      key === 'tabIndex' ||
      key === 'target' ||
      key === 'rel' ||
      key === 'download' ||
      key === 'draggable' ||
      key === 'style' ||
      key.startsWith('data-') ||
      key.startsWith('aria-') ||
      key.startsWith('on') // event handlers like onClick
  );
  const forwardedAnchorAttrs = _.pick(urlProps, anchorAttrKeys);

  // If child is already an anchor, use it directly as the child of Next's Link (no injected <a>)
  if (hasAnchorChild) {
    // Filter out non-element children (like JSX comments) to avoid multiple children error
    const validChildren = React.Children.toArray(children).filter((child) => React.isValidElement(child));

    // Next.js Link only supports a single child, so if we have one valid child, use it directly
    // Otherwise wrap multiple children in a fragment (though this shouldn't happen in practice)
    if (validChildren.length === 1) {
      const child = validChildren[0];
      const anchorHref = linkHrefToAnchorHref(linkHref);
      const extra = anchorHref != null ? { href: anchorHref } : {};
      return (
        <Link href={linkHref} {...linkProps}>
          {React.cloneElement(child, { ...extra, ...forwardedAnchorAttrs })}
        </Link>
      );
    }

    // Fallback for multiple children - wrap in a single anchor
    return (
      <Link href={linkHref} {...linkProps}>
        <a href={linkHrefToAnchorHref(linkHref)} {...forwardedAnchorAttrs}>
          {validChildren}
        </a>
      </Link>
    );
  }

  return (
    <Link href={linkHref} scroll={scroll} prefetch={prefetch} {...forwardedAnchorAttrs}>
      {children}
    </Link>
  );
}
